import React, { useCallback, useEffect } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Comment from "antd/es/comment";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Avatar, Button } from "@mapmycustomers/ui";

import Lexer from "@app/component/Lexer";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import MultilineText from "@app/component/typography/MultilineText";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatRawDateRelative, getUserDisplayNameWithInfo } from "@app/util/formatters";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";
import useSizeObserver from "@app/util/hook/useSizeObserver";

import EditNoteForm from "./EditNoteForm";
import styles from "./NoteItem.module.scss";

export interface NoteItemProps {
  canEdit?: boolean;
  deleteLoading?: boolean;
  index: number;
  note: Note;
  onChange?: (note: Note) => void;
  onDelete?: (note: Note) => void;
  onHeightChange?: (note: Note, index: number, height: number) => void;
  onNotesChange?: (notesChanged: boolean) => void;
  saveLoading?: boolean;
  teams: Team[];
  users: User[];
}

const NoteItem: React.FC<NoteItemProps> = ({
  canEdit = true,
  deleteLoading,
  index,
  note,
  onChange,
  onDelete,
  onHeightChange,
  onNotesChange,
  saveLoading,
  teams,
  users,
}) => {
  const intl = useIntl();
  const [isEditing, startEditing, stopEditing] = useBoolean();

  useModalVisibilityAnalyticEffect("Edit Note", isEditing, useAnalytics());

  useEffect(() => {
    onNotesChange?.(isEditing);
  }, [isEditing, onNotesChange]);

  const [, height, ref] = useSizeObserver<HTMLDivElement>();
  useEffect(() => {
    if (height > 0) {
      onHeightChange?.(note, index, height);
    }
  }, [height, note, index, onHeightChange]);

  const handleDelete = useCallback(() => {
    stopEditing();
    onDelete?.(note);
  }, [note, onDelete, stopEditing]);

  return (
    <div ref={ref}>
      <Comment
        author={getUserDisplayNameWithInfo(intl, note.user)}
        avatar={<Avatar tooltip={false} user={note.user} />}
        className={styles.container}
        content={
          <>
            {!isEditing && (
              <Tooltip
                placement="left"
                title={intl.formatMessage({
                  id: "recordPane.notes.edit.uneditable",
                  defaultMessage:
                    "You do not have permission to use this feature. Please contact your admin to request a change.",
                  description:
                    "Tooltip for the Edit note button in notes panel when user has no edit permissions",
                })}
                trigger={canEdit ? [] : ["hover"]}
              >
                <Button
                  className={styles.editButton}
                  disabled={!canEdit}
                  leftIcon={<FontAwesomeIcon icon={faPen} />}
                  onClick={startEditing}
                  size="small"
                  type="link"
                >
                  {intl.formatMessage({
                    id: "recordPane.notes.edit",
                    defaultMessage: "Edit",
                    description: "Edit note button in notes panel in *Edit panes",
                  })}
                </Button>
              </Tooltip>
            )}
            {isEditing ? (
              <EditNoteForm
                isDeleting={deleteLoading}
                isSaving={saveLoading}
                noteText={note.note}
                onCancel={stopEditing}
                onDelete={handleDelete}
                onSave={(noteText: string) => {
                  stopEditing();
                  onChange?.({ ...note, note: noteText });
                }}
              />
            ) : (
              <LongContentParagraph noMargin>
                <MultilineText wrapLinesWith={Lexer} wrapperProps={{ teams, users }}>
                  {note.note}
                </MultilineText>
              </LongContentParagraph>
            )}
          </>
        }
        datetime={formatRawDateRelative(note.createdAt, Date.now(), "", "PPPp")}
      />
    </div>
  );
};

export default NoteItem;
